import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";

const COOKIE_KEY = "hp_cookie_consent";

const getCookieConsent = () => {
  try {
    const stored = localStorage.getItem(COOKIE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveCookieConsent = (consent) => {
  localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { necessary: true, analytics: true, marketing: true };
    saveCookieConsent(consent);
    setVisible(false);
  };

  const handleRejectAll = () => {
    const consent = { necessary: true, analytics: false, marketing: false };
    saveCookieConsent(consent);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveCookieConsent(preferences);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-[#19294C] rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
            {/* Main banner */}
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden md:flex w-10 h-10 rounded-xl bg-[#00B8E4]/15 items-center justify-center shrink-0 mt-0.5">
                  <Shield className="w-5 h-5 text-[#00B8E4]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold font-['Outfit'] text-base mb-1.5">
                    Respect de votre vie privée
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu.
                    Consultez notre{" "}
                    <Link
                      to="/politique-de-confidentialite"
                      className="text-[#00B8E4] hover:underline"
                      data-testid="cookie-privacy-link"
                    >
                      politique de confidentialité
                    </Link>{" "}
                    pour en savoir plus.
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 mt-5">
                <button
                  onClick={handleAcceptAll}
                  className="bg-[#00B8E4] text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-[#00a5ce] transition-colors"
                  data-testid="cookie-accept-all"
                >
                  Tout accepter
                </button>
                <button
                  onClick={handleRejectAll}
                  className="border border-white/20 text-white/80 text-sm font-medium px-6 py-2.5 rounded-full hover:bg-white/5 transition-colors"
                  data-testid="cookie-reject-all"
                >
                  Tout refuser
                </button>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center justify-center gap-1.5 text-white/50 text-sm font-medium px-4 py-2.5 hover:text-white/80 transition-colors sm:ml-auto"
                  data-testid="cookie-customize-btn"
                >
                  Personnaliser
                  {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Details panel */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 md:px-6 pb-5 md:pb-6 border-t border-white/10 pt-5 space-y-3">
                    {/* Necessary */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">Cookies nécessaires</p>
                        <p className="text-white/40 text-xs mt-0.5">Fonctionnement du site et sécurité</p>
                      </div>
                      <div className="w-10 h-5 bg-[#00B8E4] rounded-full relative cursor-not-allowed opacity-60">
                        <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">Cookies analytiques</p>
                        <p className="text-white/40 text-xs mt-0.5">Google Analytics — mesure d'audience</p>
                      </div>
                      <button
                        onClick={() => setPreferences((p) => ({ ...p, analytics: !p.analytics }))}
                        className={`w-10 h-5 rounded-full relative transition-colors ${
                          preferences.analytics ? "bg-[#00B8E4]" : "bg-white/20"
                        }`}
                        data-testid="cookie-toggle-analytics"
                      >
                        <div
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
                            preferences.analytics ? "right-0.5" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Marketing */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-medium">Cookies marketing</p>
                        <p className="text-white/40 text-xs mt-0.5">Meta Pixel — publicité ciblée</p>
                      </div>
                      <button
                        onClick={() => setPreferences((p) => ({ ...p, marketing: !p.marketing }))}
                        className={`w-10 h-5 rounded-full relative transition-colors ${
                          preferences.marketing ? "bg-[#00B8E4]" : "bg-white/20"
                        }`}
                        data-testid="cookie-toggle-marketing"
                      >
                        <div
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
                            preferences.marketing ? "right-0.5" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>

                    <button
                      onClick={handleSavePreferences}
                      className="w-full mt-3 bg-white/10 text-white text-sm font-medium py-2.5 rounded-full hover:bg-white/15 transition-colors"
                      data-testid="cookie-save-preferences"
                    >
                      Enregistrer mes préférences
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
